import React, { useState } from 'react';
import { DocumentLine, FileUploadButton } from '@/components/common/atoms';
import { Col, Container, ExternalLink, Span, Flex, Text } from '@/ui';
import { DISPLAY_DOC_SUBTYPE } from '@/utils/static/kyc';
import { API_URL, COLORS } from '@/utils/static';
import { useTranslation } from 'react-i18next';
import { getLocalizedText } from '@/utils/helpers';
import { Label } from '@/components/common';
import { Dropdown, Input } from 'antd';
import styled from 'styled-components';

export const UploadDocumentsBox = ({
  label,
  extensions,
  tooltip,
  tooltip_RU,
  tooltip_LV,
  maxSize,
  required = false,
  docs,
  setDocs,
  types = null,
  setType,
  enableNumbering,
  multiple = false,
  hideLastBorder,
  buttonProps,
  template = null,
  isReadTooltip = true,
  withInput = false,
  documentName,
  setDocumentName
}) => {
  const { t, i18n } = useTranslation();
  const [selected, setSelected] = useState(docs ? new Array(docs.length).fill(1) : []);

  const addDocs = (selectedDocs) => {
    if (multiple) {
      const docsClone = [...docs];
      [...selectedDocs].forEach((item) => {
        docsClone.push(item);
      });

      setDocs(docsClone);
    } else {
      setDocs(selectedDocs[0]);
    }
  };

  const deleteDoc = (id) => {
    if (multiple) {
      setDocs(docs.filter((item, key) => key !== id));
    } else {
      setDocs(null);
    }
  };

  const handleSelected = (nextSelected, index) => {
    const clone = [...selected];
    clone[index] = nextSelected;
    setSelected(clone);
    setType(types[nextSelected], index);
  };

  return (
    <Container>
      <Label
        required={required}
        {...(label
          ? {
              label: (
                <>
                  <Flex gap={10} wrap>
                    {label}
                    {extensions && (
                      <Span $color={COLORS.orange} style={{ fontSize: '11px' }}>
                        (
                        {extensions.reduce(
                          (res, item, index) => res + (index > 0 ? ', ' : '') + item.label,
                          ''
                        )}
                        )
                      </Span>
                    )}
                    {maxSize && (
                      <Span $color={COLORS.greyMedium}>
                        {t('Max. size')} - {maxSize}
                      </Span>
                    )}
                  </Flex>
                </>
              )
            }
          : {})}
        tooltip={
          tooltip
            ? getLocalizedText(
                {
                  tooltip: tooltip,
                  tooltip_RU: tooltip_RU,
                  tooltip_LV: tooltip_LV
                },
                'tooltip',
                i18n
              )
            : null
        }
      >
        {docs && docs.length > 0 && (
          <Container>
            {types
              ? docs.map((doc, key) => (
                  <Flex align='center' $margin={-8} key={key} wrap>
                    <Col>
                      <DocumentLine
                        doc={{
                          name: doc.name,
                          documentURL: doc instanceof File ? URL.createObjectURL(doc) : doc,
                          id: key
                        }}
                        onDelete={(id) => deleteDoc(id)}
                        index={enableNumbering && key + 1}
                        hideLastBorder={hideLastBorder}
                        key={key}
                      />
                    </Col>
                    <Col>
                      <Dropdown
                        menu={{
                          items: types.map((item) => ({
                            key: item,
                            label: DISPLAY_DOC_SUBTYPE[item]
                          })),
                          onClick: (val) => handleSelected(val, key)
                        }}
                        trigger={'click'}
                      />
                    </Col>
                  </Flex>
                ))
              : docs.map((doc, key) => (
                  <>
                    {withInput ? (
                      <DocumentWithInputWrapper vertical>
                        <DocumentLine
                          doc={{
                            name: doc.name,
                            documentURL: doc instanceof File ? URL.createObjectURL(doc) : doc,
                            id: key
                          }}
                          onDelete={(id) => deleteDoc(id)}
                          index={enableNumbering && key + 1}
                          hideBorder
                          key={key}
                        />
                        <Text
                          $color={COLORS.greyText}
                          $fontSize={13}
                          $marginTop={10}
                          $marginBottom={6}
                        >
                          Enter document name:
                        </Text>
                        <Input
                          value={documentName}
                          onChange={(e) => setDocumentName(e.target.value)}
                        />
                      </DocumentWithInputWrapper>
                    ) : (
                      <DocumentLine
                        doc={{
                          name: doc.name,
                          documentURL: doc instanceof File ? URL.createObjectURL(doc) : doc,
                          id: key
                        }}
                        onDelete={(id) => deleteDoc(id)}
                        index={enableNumbering && key + 1}
                        hideLastBorder={hideLastBorder}
                        key={key}
                      />
                    )}
                  </>
                ))}
          </Container>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', marginTop: '3px' }}>
          <FileUploadButton
            buttonProps={{
              text: multiple ? t('Add files') : t('Add file'),
              size: 'small',
              ...buttonProps
            }}
            onChange={addDocs}
            multiple={multiple}
            isReadTooltip={isReadTooltip}
          />
          {template && (
            <ExternalLink href={`${API_URL}${template}`}>{t('Document template')}</ExternalLink>
          )}
        </div>
      </Label>
    </Container>
  );
};

const DocumentWithInputWrapper = styled(Flex)`
  padding: 3px 15px 15px;
  border: 1px solid ${COLORS.greyBasic};
  border-radius: 8px;
  margin-bottom: 20px;
`;
